import * as React from 'react';
import { Box, Modal, Popover } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TenkeyType } from 'types/support/supportType';
import CloseButton from 'components/support/atoms/buttons/CloseButton';
import NumberButton from 'components/support/atoms/buttons/NumberButton';
import PlusMinusButton from 'components/support/atoms/buttons/PlusMinusButton';
import DeleteButton from 'components/support/atoms/buttons/DeleteButton';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import { useCallback, useState } from 'react';
import { useAppDispatch } from 'redux/hooks';
import MESSAGES from 'constants/messages';
import { handleCallFrontError } from 'redux/slices/commonSlice';
import MessageHelper from 'utils/message.helper';
import COMMON from 'constants/common';
import { InfoDialog } from '../../../system/organisms';

/* ************* constant ************* */
const DECIMAL_POINT = '.';

/* ************ Style ************ */
const useStyles = makeStyles()((theme) => ({
  root: {},
  paper: {
    backgroundColor: 'transparent',
    height: '576px',
  },
  baseArea: {
    display: 'flex',
  },
  inputArea: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  inputNumberArea: {
    width: '276px',
    paddingTop: '4px',
  },
  plusMinusArea: {
    padding: '28px 0px 0px 8px',
    width: '92px',
  },
  tenkey: {
    backgroundColor: theme.palette.common.black,
    borderRadius: '12px',
    height: '572px',
    padding: '12px 16px',
    margin: 0,
    width: '400px',
  },
  inputNumber: {
    backgroundColor: theme.palette.common.white,
    borderRadius: '16px',
    fontSize: '70px',
    height: '84px',
    paddingRight: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '368px',
  },
  inputNumberError: {
    color: 'red',
  },
  inputNumberNormal: {
    color: 'black',
  },
  numberButton: {
    border: '1px solid #707070',
    fontSize: '70px',
    height: '84px',
    margin: '8px 8px 0px 0px',
    width: '84px',
  },
  deleteButton: {
    height: '84px',
    margin: '8px 8px 0px 0px',
    width: '84px',
  },
  plusMinusButton: {
    height: '154px',
    marginBottom: '24px',
    width: '84px',
  },
  decisionButton: {
    backgroundColor: '#FF3E3E',
    color: theme.palette.common.white,
    fontSize: '56px',
    height: '80px',
    marginTop: '4px',
    width: '368px',
    '&:hover': {
      backgroundColor: '#FF3E3E',
    },
    '&:active': {
      backgroundColor: 'rgba(255, 62, 62, 0.8)',
    },
  },
}));

/* *************    constants   ************* */
export const INITIAL_TEN_KEY: { tenkey: TenkeyType } = {
  tenkey: {
    anchorPosition: { top: 100, left: 0 },
    name: '',
    open: false,
    option: {
      mode: 0,
      integerSize: 3,
      scaleSize: 1,
      maxValue: 999,
      minValue: 0,
    },
    value: '',
    onDecision: () => undefined,
  },
};

/* ************* local function ************* */
const toNumberString = (numberValue: string) => (!numberValue || numberValue === DECIMAL_POINT ? '0' : numberValue);
const toValueString = (value: string) => (!value || value === 'null' ? '' : value);

const calcUpDown = (numberValue: string, incrementValue: number) => {
  // 小数を含んだ値を計算すると値がおかしくなるため整数部を取り出して計算する。
  const numberSplit = numberValue.split(DECIMAL_POINT);
  // 整数部を取り出して計算。計算後の値に小数点と小数部を追加
  return (
    String(Number(numberSplit[0]) + incrementValue) + (numberSplit.length > 1 ? DECIMAL_POINT + numberSplit[1] : '')
  );
};

/* ************ Component ************ */
/* ********** Main Component ********* */
/**
 * Tenkey テンキー
 * 数値を入力するためのテンキー画面を表示する。
 *
 * @author atsushi.teruya
 * @Params {SelectListType} selectList - 一覧選択の情報
 * @returns {React.FC} - 一覧選択の表示
 *
 */
const Tenkey: React.FC<React.PropsWithChildren<{ tenkey: TenkeyType }>> = ({ tenkey }) => {
  const { anchorPosition, name, open, option, value, onDecision, onClose } = tenkey;
  const { classes } = useStyles();
  const [inputNumber, setInputNumber] = useState(toValueString(value));
  const [errorValue, setErrorValue] = useState(false);
  // State dialog
  const [showDialog, setShowDialog] = React.useState({ isShow: false, message: '' });

  /* *************      hooks     ************* */
  // 表示のトリガーに利用した一覧選択ボタンを前面に表示する。
  const dispatch = useAppDispatch();

  // valueの初期化 open,closeのタイミングで初期化
  React.useEffect(() => {
    setInputNumber(toValueString(''));
  }, [open]);

  /**
   * handle change value and show error
   */
  React.useEffect(() => {
    const { minValue, maxValue } = option;
    const valueNumber = Number(inputNumber || '0');
    if (valueNumber < minValue || valueNumber > maxValue) {
      setErrorValue(true);
    } else {
      setErrorValue(false);
    }
  }, [inputNumber]);

  /* *************      event     ************* */
  // NoX. 画面の外クリック
  // ダイアログ閉じる
  const handleClose = (event: Record<string, unknown>, reason: 'backdropClick' | 'escapeKeyDown') => {
    // 表示のトリガーに利用した項目の表示位置をもとに戻す。
    // 親から渡された処理があれば実行する。
    if (onClose) onClose(event, reason);
  };

  // No0. ページ読み込み
  // コンポーネントの表示制御だけなのでjsx内で対応する。 入力数値の初期表示、小数点の表示制御

  // No1. 閉じるボタンクリック
  const handleClickClose: React.MouseEventHandler<HTMLButtonElement> = () => {
    // 1-1. 画面を閉じ、モーダル表示を解除する。
    handleClose({}, 'backdropClick');
  };

  // No2～4,6～11,14,15. 0～9,小数点クリック
  const handleNumberClick =
    (decimalValue: string): React.MouseEventHandler<HTMLButtonElement> =>
    () => {
      const { integerSize, scaleSize } = option;

      // 空文字を含む数字以外の値や小数点だけの場合は0に変換。
      const numberValue = toNumberString(inputNumber);
      // 計算結果の想定値を初期設定
      let resultValue = numberValue + decimalValue;

      // 1-1. 入力値チェック
      const numberSplit = numberValue.split(DECIMAL_POINT);

      // 整数部桁数チェック 小数点または小数部の入力の場合はチェックしない。
      if (
        decimalValue !== DECIMAL_POINT &&
        !inputNumber.includes(DECIMAL_POINT) &&
        integerSize <= numberSplit[0].length
      )
        return;
      // 小数部桁数チェック
      if (numberSplit.length > 1 && scaleSize <= numberSplit[1].length) return;
      // 小数点二重チェック
      if (decimalValue === DECIMAL_POINT && numberValue.includes(DECIMAL_POINT)) return;
      // 1-2. 入力値反映
      if (numberValue === '0') {
        // 入力数値が未入力か0の場合入力数値を置き換える。
        if (decimalValue === DECIMAL_POINT) {
          // 小数点だけ入力する場合は0を先頭につける。
          resultValue = `0${decimalValue}`.slice(-2);
        } else {
          resultValue = decimalValue;
        }
      }
      setInputNumber(resultValue);
    };

  // No5 UPクリック
  const handleClickUp: React.MouseEventHandler<HTMLButtonElement> = () => {
    const { integerSize } = option;
    // 空文字を含む数字以外の値や小数点だけの場合は0に変換。
    const numberValue = toNumberString(inputNumber);
    // 1-1. 値算出
    // 計算関数で+1した値を取得。
    const resultValue = calcUpDown(numberValue, 1);
    // 1-2. 入力チェック
    // 整数部桁数チェック
    if (integerSize < resultValue.split(DECIMAL_POINT)[0].length) return;
    // 1-3. 画面に反映
    setInputNumber(resultValue);
  };

  // No12 Downクリック
  const handleClickDown: React.MouseEventHandler<HTMLButtonElement> = () => {
    // 空文字を含む数字以外の値や小数点だけの場合は0に変換。
    const numberValue = toNumberString(inputNumber);
    // 1-1. 値算出
    // 計算関数で+1した値を取得。
    const resultValue = calcUpDown(numberValue, -1);
    // 1-2. 入力チェック
    if (Number(resultValue) < 0) return;
    // 1-3. 画面に反映
    setInputNumber(String(resultValue));
  };

  // No13 削除クリック
  const handleClickDelete: React.MouseEventHandler<HTMLButtonElement> = () => {
    // 1-1. 入力チェック
    if (inputNumber) setInputNumber(inputNumber.slice(0, -1));
  };

  // No17 決定クリック
  const handleClickDecision: React.MouseEventHandler<HTMLButtonElement> = () => {
    const { minValue, maxValue } = option;

    // 入力値が数字ではない場合は空文字を設定して画面を閉じる。
    // 入力値が数字ではない場合は0とみなす。
    const valueNumber = Number(inputNumber || '0');

    // If input is empty then show error with snackbar
    if (!inputNumber && inputNumber !== '0') {
      const erros = {
        errors: { key: [MessageHelper.messageFormat(MESSAGES.INF108, String(minValue), String(maxValue))] },
      };
      dispatch(handleCallFrontError(erros));
      return;
    }

    if (valueNumber < minValue || maxValue < valueNumber) {
      setShowDialog({
        isShow: true,
        message: MessageHelper.messageFormat(MESSAGES.INF242, String(minValue), String(maxValue)),
      });
      return;
    }

    // 小数点を許容する数値の上限
    // decimalLimit で設定された値以上が入力された場合はエラーを返す
    if (option.decimalLimit) {
      if (valueNumber >= option.decimalLimit && !Number.isInteger(valueNumber)) {
        const errors = {
          errors: { key: [MessageHelper.messageFormat(MESSAGES.INF113, String(option.decimalLimit))] },
        };
        dispatch(handleCallFrontError(errors));
        return;
      }
    }

    // 2. 画面を閉じる。
    // 2-2. 入力数値の値を呼び出し元の画面に返却する。そのために親コンポーネントの関数を実行する。
    const returnCheck = inputNumber.slice(-1);
    onDecision(returnCheck === DECIMAL_POINT ? inputNumber.slice(0, -1) : inputNumber, name);
    handleClose({}, 'backdropClick');
  };

  /**
   * Handle error close dialog info
   */
  const handleErrorClose = useCallback(() => {
    setShowDialog({ isShow: false, message: '' });
    const { minValue, maxValue } = option;
    const valueNumber = Number(inputNumber || '0');
    if (valueNumber > maxValue) {
      setInputNumber(toValueString(maxValue.toString()));
    }
    if (valueNumber < minValue) {
      setInputNumber(toValueString(minValue.toString()));
    }
  }, [inputNumber]);

  return (
    <>
      <Modal className={classes.root} open={open} onClose={handleClose} disableRestoreFocus>
        <Popover
          open={open}
          anchorPosition={anchorPosition}
          anchorReference="anchorPosition"
          classes={{
            root: classes.root,
            paper: classes.paper,
          }}
          onClose={handleClose}
          elevation={0}>
          <Box className={classes.baseArea}>
            <CloseButton onClick={handleClickClose}>Ｘ 閉じる</CloseButton>
            <Box className={classes.tenkey}>
              <Box
                className={`${classes.inputNumber} ${
                  errorValue ? classes.inputNumberError : classes.inputNumberNormal
                }`}>
                {inputNumber}
              </Box>
              <Box className={classes.inputArea}>
                <Box className={classes.inputNumberArea}>
                  <Box>
                    <NumberButton className={classes.numberButton} value="7" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="8" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="9" onNumberClick={handleNumberClick} />
                  </Box>
                  <Box>
                    <NumberButton className={classes.numberButton} value="4" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="5" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="6" onNumberClick={handleNumberClick} />
                  </Box>
                  <Box>
                    <NumberButton className={classes.numberButton} value="1" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="2" onNumberClick={handleNumberClick} />
                    <NumberButton className={classes.numberButton} value="3" onNumberClick={handleNumberClick} />
                  </Box>
                  <Box>
                    <DeleteButton className={classes.deleteButton} onClick={handleClickDelete} />
                    <NumberButton className={classes.numberButton} value="0" onNumberClick={handleNumberClick} />
                    {/* Event No0.ページ読み込み 小数点の表示を制御 */}
                    {option.mode === COMMON.TENKEY_MODE.DECIMAL && (
                      <NumberButton className={classes.numberButton} value="." onNumberClick={handleNumberClick} />
                    )}
                  </Box>
                </Box>
                <Box className={classes.plusMinusArea}>
                  <PlusMinusButton isPlus className={classes.plusMinusButton} onClick={handleClickUp} />
                  <PlusMinusButton isPlus={false} className={classes.plusMinusButton} onClick={handleClickDown} />
                </Box>
              </Box>
              <NormalButton className={classes.decisionButton} onClick={handleClickDecision}>
                決 定
              </NormalButton>
            </Box>
          </Box>
        </Popover>
      </Modal>
      <InfoDialog open={showDialog.isShow} msg={showDialog.message} closeFunc={handleErrorClose} />
    </>
  );
};

/* ********** Initial value ********** */
Tenkey.defaultProps = INITIAL_TEN_KEY;
export default Tenkey;
